/**
 * Generates the expansion string for a Lindenmayer System (L-System).
 * * MATHEMATICAL CORE:
 * This function performs the recursive string rewriting logic. It starts with an "Axiom"
 * and applies a set of "Production Rules" for a specified number of iterations.
 *
 * * HOW IT WORKS:
 * 1. Starts with the initial state (e.g., "X").
 * 2. In each iteration, it loops through every character of the current string.
 * 3. If a character matches a Rule (e.g., "F" -> "FF+[+F]"), it replaces it.
 * 4. If no rule exists, the character remains unchanged.
 *
 * @param {string} axiom - The initial string (the "seed" of the fractal).
 * @param {object} rules - A key-value map defining how characters are replaced (e.g., { F: "FF" }).
 * @param {number} iterations - The depth of recursion (how many times to rewrite the string).
 * @returns {string} The final expanded string containing the drawing instructions.
 */
export const generateLSystem = (axiom, rules, iterations) => {
    let sentence = axiom;
    for (let i = 0; i < iterations; i++) {
        let nextSentence = "";
        for (let j = 0; j < sentence.length; j++) {
            let char = sentence.charAt(j);
            if (rules[char]) nextSentence += rules[char];
            else nextSentence += char;
        }
        sentence = nextSentence;
    }
    return sentence;
};

/**
 * Interprets the L-System string and draws it using "Turtle Graphics" logic.
 * * RENDERING ENGINE:
 * This function translates the abstract string characters into visual p5.js commands.
 * It simulates a "turtle" moving on the canvas, drawing lines and turning.
 *
 * * INSTRUCTION MAPPING:
 * - 'F': Draw a line forward (Step Size: len) and move the turtle.
 * - '+': Turn right by the specific angle.
 * - '-': Turn left by the specific angle.
 * - '[': PUSH Matrix (Save current position & angle) -> Starts a new branch.
 * - ']': POP Matrix (Restore previous position & angle) -> Returns to the main stem.
 *
 * * VISUALS:
 * Applies a slight hue shift based on the character index to create a gradient effect.
 *
 * @param {object} p5 - The p5.js instance.
 * @param {string} sentence - The L-System instruction string generated by generateLSystem.
 * @param {number} len - The length of each line segment (Step size).
 * @param {number} angle - The rotation angle in radians for '+' and '-' commands.
 * @param {number} hueBase - The starting Hue value (0-360).
 * @param {number} satBase - The Saturation value (0-100).
 * @returns {void} Directly modifies the p5 canvas.
 */
export const renderTurtle = (p5, sentence, len, angle, hueBase, satBase) => {
  for (let i = 0; i < sentence.length; i++) {
    let char = sentence.charAt(i);
    if (char === "F") {
      p5.stroke(hueBase + (i % 20), satBase, 95, 0.6);
      p5.line(0, 0, 0, -len);
      p5.translate(0, -len);
    } else if (char === "+") {
      p5.rotate(angle);
    } else if (char === "-") {
      p5.rotate(-angle);
    } else if (char === "[") {
      p5.push();
    } else if (char === "]") {
      p5.pop();
    }
  }
};

/**
 * Orchestrates the creation of a symmetrical Mandala/Flower pattern.
 * * SYMMETRY LOGIC:
 * Instead of drawing a single fractal tree, this function repeats the drawing
 * multiple times in a circle (Radial Symmetry) to create a hypnotic, breathing effect.
 *
 * * PROCESS:
 * 1. Loops 'symmetry' times (e.g., 6 times for a hexagon-like structure).
 * 2. Rotates the entire canvas context by (360° / symmetry) in each step.
 * 3. Calls 'renderTurtle' to draw one branch of the fractal.
 * 4. Uses push/pop to isolate the transformations of each branch.
 *
 * @param {object} p5 - The p5.js instance.
 * @param {string} sentence - The L-System instruction string.
 * @param {number} symmetry - Number of repetitions in the circle (e.g., 6, 8, 12).
 * @param {number} len - Length of the line segments.
 * @param {number} angle - Fractal branching angle.
 * @param {number} hue - Color Hue.
 * @param {number} sat - Color Saturation.
 * @param {number} weight - Stroke weight (thickness of the lines).
 * @returns {void} Draws the complete ring to the canvas.
 */
export const drawFractalRing = (p5, sentence, symmetry, len, angle, hue, sat, weight) => {
  for (let i = 0; i < symmetry; i++) {
    p5.rotate(p5.TWO_PI / symmetry);
    p5.push();
    p5.strokeWeight(weight);
    renderTurtle(p5, sentence, len, angle, hue, sat);
    p5.pop();
  }
};